import type { LegalContentBlock } from './legal-content';

export const PRIVACY_POLICY_CONTENT_BLOCKS: LegalContentBlock[] = [
  {
    type: 'title',
    runs: [
      {
        text: 'Privacy Policy for CareCal',
      },
    ],
  },
  {
    type: 'date',
    runs: [
      {
        text: 'Last modified: July 21, 2026',
        italic: true,
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'CareCal helps caregivers create a digital profile calendar to share with clients for easy and efficient scheduling and bookings. In order to serve our mission, we must collect personal information from users of our website and our services. With that said, CareCal respects your privacy and is committed to protecting it through our compliance with this policy.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Introduction AND APPLICABILITY',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'This policy describes the types of information BookLilliCares LLC dba CareCal (“Company” or “We” or “Us”) may collect from you or that you may provide when you visit the website ',
      },
      {
        text: 'www.mycarecal.com',
        href: 'https://www.mycarecal.com',
      },
      {
        text: ' (our “Website”), use our Services/Platform (as defined under our Terms and Conditions (',
      },
      {
        text: 'https://www.mycarecal.com/terms-and-conditions',
        href: 'https://www.mycarecal.com/terms-and-conditions',
      },
      {
        text: '), or interact with any of our services, and our practices for collecting, using, maintaining, protecting, and disclosing that information.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'This policy applies to information we collect:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'On this Website or through the Services/Platform.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'In email, text, and other electronic messages between you and this Website and/or the Services/Platform.',
      },
    ],
    level: 1,
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'It does not apply to information collected by:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Us offline or through any other means, including on any other website operated by Company or any third party that does not link to this policy; or ',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Any third party, including through any application or content (including advertising) that may link to or be accessible from or through the Website.',
      },
    ],
    level: 1,
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Please read this policy carefully to understand our policies and practices regarding your information and how we will treat it. ',
      },
      {
        text: 'If you do not agree with our policies and practices, your choice is not to use our Website and/or the Platform.',
        bold: true,
      },
      {
        text: ' By accessing or using this Website or the Platform, you agree to this privacy policy. This policy may change from time to time (see Changes to our Privacy Policy below). Your continued use of this Website or the Platform after we make changes is deemed to be acceptance of those changes, so please check the policy periodically for updates. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Children AND MINOR’S DATA',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Our Website and Platform is not intended for children under 18 years of age without the consent of a parent or legal guardian as described in the Teen Users section of our Terms ',
      },
      {
        text: 'https://www.mycarecal.com/terms-and-conditions',
        href: 'https://www.mycarecal.com/terms-and-conditions',
      },
      {
        text: '. We do not knowingly collect personal information from children under the age of 13. If you are under 13, do not use or provide any information on this Website or through any of its features, register on the Platform, or otherwise provide any information about yourself to us, including your name, address, telephone number, or email address. If you are between the ages of 13 and 18, but do not have parental or legal guardian consent pursuant to our Terms, do not use or provide any information on this Website or through any of its features, register on the Platform, or otherwise provide any information about yourself to us, including your name, address, telephone number, or email address. If we learn we have collected or received personal information from a child under 13 or between the ages of 13 and 18 without verification of appropriate parental or legal guardian consent, we will make commercially reasonable efforts to delete such personal information from our database. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Information We Collect About You and How We Collect It',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'We collect several types of information from and about users of our Website and Services/Platform, including information you directly provide to us, information we automatically collect as described below, and information from third parties, for example, reviews received about you by other Users. ',
      },
    ],
    level: 1,
  },
  {
    type: 'subheading',
    runs: [
      {
        text: 'Information You Provide ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We collect several types of information from and about users of our Website and Platform that you voluntary provide, including:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Registration Information',
        bold: true,
      },
      {
        text: '. When registering for our Platform or making purchases through our Website, we will collect and store the information that the User provides to us such as name, postal address, email address, telephone number, and other contact information you provide to us.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Additional Account Information.',
        bold: true,
      },
      {
        text: ' When using our Services/Platform, we collect and store all of the additional information you provide, which may include, pictures you chose to provide, your schedule and availability, location, and any other information the Users chose to share with one another while using the Services/Platform.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'User Content.',
        bold: true,
      },
      {
        text: ' We collect and store all User Content (as defined under our Terms and Conditions. Please be aware, we cannot control the actions of other Users of the Services/Platform with whom you may choose to share your User Content.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Billing Information.',
        bold: true,
      },
      {
        text: ' Our third party payment processes will collect and store your billing address and credit card information. CareCal will collect and store your billing address, credit card information, and billing records and receipts of payments made.  ',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Educational and Marketing Information.',
        bold: true,
      },
      {
        text: ' You may voluntarily provide us information such as name, email address, or other contact information, through forms made available on our Website if you are interested in our newsletter or other promotional information available on the Website. ',
      },
    ],
    level: 1,
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Information Provided by Others',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We collect and store information that Users and other Website visitors provide about one another. For example, reviews of Users provided by other Users, or communications directly between Users on the Platform. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Information We Collect Through Automatic Data Collection Technologies  ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'As you navigate through and interact with our Website or Services/Platform, we may use automatic data collection technologies to collect certain information about your equipment, browsing actions, and patterns, including:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: ' Details of your visits to our Website, including traffic data, location data, logs, usage date and other communication data and the resources that you access and use on the Website.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Information about your computer and internet connection, including your IP address, operating system, and browser type.',
      },
    ],
    level: 1,
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Some browsers used to access the Internet may give you the option through “Do Not Track” settings associated with such browsers to request that a website operator not track your information across its website or other websites. We engage in such tracking in order to optimize and/or customize our Website, detect and respond to allegations of fraud, and to otherwise ensure the safety of our Users and visitors of our Website. While we do not currently honor such browser requests, we will not use your information in a manner that violates this Privacy Policy. ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The information we collect automatically is only statistical data and does not include personal information, or we may maintain it or associate it with personal information we collect in other ways or receive from third parties. It helps us to improve our Website and to deliver a better and more personalized service, including by enabling us to:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Estimate our audience size and usage patterns.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Store information about your preferences, allowing us to customize our Website according to your individual interests.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Speed up your searches.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Recognize you when you return to our Website.',
      },
    ],
    level: 1,
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The technologies we use for this automatic data collection may include:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Cookies (or browser cookies).',
        bold: true,
      },
      {
        text: ' A cookie is a small file placed on the hard drive of your computer. You may refuse to accept browser cookies by activating the appropriate setting on your browser. However, if you select this setting you may be unable to access certain parts of our Website. Unless you have adjusted your browser setting so that it will refuse cookies, our system will issue cookies when you direct your browser to our Website. ',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Web Beacons.',
        bold: true,
      },
      {
        text: ' Pages of our Website and our emails may contain small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages or opened an email and for other related website statistics (for example, recording the popularity of certain website content and verifying system and server integrity).',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'When you interact with the Website there are third parties that may use automatic collection technologies to collect information about your or your device. These third parties may include advertisers, ad networks and servers, and application providers. These third parties may use tracking technologies to collect information about you when you use the Website. The information they collect may be associated with your personal data or they may collect information, including personal data, about your online activities over time and across different websites, apps, platforms, and other online services. ',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: "We do not control these third parties' tracking technologies or how they may be used. If you have any questions about an advertisement or other targeted content, you should contact the responsible provider directly.",
      },
    ],
    level: 1,
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'How We Use Your Information',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We use information that we collect about you or that you provide to us, including any personal information:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Provide you with the Services and any contents, features, information, products, or services that we make available through the Website. To fulfill any other purpose for which you provide it.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'To provide you with notices about your subscription, including expiration and renewal notices.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Improve our Services, including by analyzing your information and creating aggregated data derived from your information) to develop, maintain, analyze, improve, optimize, measure, and report on our Services and their features and how users interact with them.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'If you are a User of the Services/Platform, to promote the Platform and its community. This may include featuring your profile or conte on our Website, in marketing materials, and on our social medial channels, including sharing links to your profile or user page. Where required by applicable law, we will obtain your consent before using your personal information for promotional purposes. You may opt out of such uses at any time by contacting us at ',
      },
      {
        text: 'support@carecal.net',
        href: 'mailto:support@carecal.net',
      },
      {
        text: '.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'To carry out our obligations and enforce our rights arising from any contracts entered into between you and us, including for billing and collection.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'To notify you about changes to our Website or any products or services we offer or provide though it.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'In any other way we may describe when you provide the information.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'For any other purpose with your consent.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'As a visitor to the Website, you can engage in most activities without providing any personal information. It is only when you seek to register for services and/or make a purchase that you are required to provide information. ',
      },
    ],
    level: 1,
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Disclosure of Your Information',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We may disclose aggregated information about our users, and information that does not identify any individual, without restriction. ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We may disclose personal information that we collect or you provide as described in this privacy policy:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'To our subsidiaries and affiliates.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'To contractors, service providers, and other third parties we use to support our business and who are bound by contractual obligations to keep personal information confidential and use it only for the purposes for which we disclose it to them.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'To a buyer or other successor in the event of a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of the Company’s assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which personal information held by the Company about our Website users is among the assets transferred.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'For any other purpose disclosed by us when you provide the information.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'With your consent.',
      },
    ],
    level: 1,
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We may also disclose your personal information:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'To comply with any court order, law, or legal process, including to respond to any government or regulatory request.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'To enforce or apply our ',
      },
      {
        text: 'Terms and Conditions',
        underline: true,
      },
      {
        text: ' ',
      },
      {
        text: 'https://www.mycarecal.com/terms-and-conditions',
        href: 'https://www.mycarecal.com/terms-and-conditions',
      },
      {
        text: ' and other agreements, including for billing and collection purposes.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'If we believe disclosure is necessary or appropriate to protect the rights, property, or safety of the Company, our customers, or others. ',
      },
    ],
    level: 1,
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Choices About How We Use and Disclose Your Information',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'You have certain rights with respect to your personal data, as detailed herein. Please note we reserve the right to request that you provide us with evidence of your identity before we take any action with respect to the exercise of your data rights. Further, your rights may be restricted or nullified to the extent they conflict with our compelling business interests, the public interest, or the law. ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Update Account Information. ',
        bold: true,
      },
      {
        text: 'You have the right to update or change any information you have provided to us. Users can review and change personal information by logging into the Platform and visiting your account profile page.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Change Consent.',
        bold: true,
      },
      {
        text: ' You may withdraw your consent to our use of your personal data. This may require deletion of your account and could limit your access to our Website or any services you have purchased through the Website.',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Tracking Technologies and Advertising.',
        bold: true,
      },
      {
        text: ' You can set your browser to refuse all or some browser cookies, or to alert you when cookies are being sent. If you disable or refuse cookies, please note that some parts of this site may then be inaccessible or not function properly.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Promotional Offers from the Company.',
        bold: true,
      },
      {
        text: ' If you do not wish to have your email address used by the Company to promote our own or third parties’ products or services, you can opt-out by sending us an email stating your request to support@carecal.net. This opt out does not apply to information provided to the Company as a result of a product purchase, warranty registration, product service experience or other transactions. ',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Access and Data Portability. ',
        bold: true,
      },
      {
        text: 'You may confirm whether we process your personal data and access a copy of the personal data we process. To the extent feasible and required by state law, depending on your state, data will be provided in a portable format. Depending on your state, you may have the right to receive additional information and it will be included in the response to your access request.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Deletion.',
        bold: true,
      },
      {
        text: ' You may request that we delete personal data about you that we maintain, subject to certain exception under applicable law. Please note, this may require deletion of your account and could limit your access to our Website or any services you have purchased through the Website.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Transfer Your Data.',
        bold: true,
      },
      {
        text: ' You have the right to request that we gather and transfer your data to another controller, in a commonly used and machine-readable format, unless doing so would cause us an undue burden.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Processing.',
        bold: true,
      },
      {
        text: ' You may request restrictions on the processing of your data in certain circumstances, such as when contesting accuracy. During restriction, we will store your data but not process it further without your consent or legal requirement. You may also opt out of all data processing, which may result in account termination.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Complaints.',
        bold: true,
      },
      {
        text: ' You have the right to complain to a supervising authority if you believe we are misusing your data or have violated any of your rights under this Privacy Policy or applicable law.',
      },
    ],
    level: 1,
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Your State Privacy Rights',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'State consumer privacy laws may provide their residents with additional rights regarding our use of their personal information. Depending on your state of residency, you may have certain rights related to your personal data, including:',
      },
    ],
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Confirming whether we process your personal information and access a copy of the personal data we process. To the extent feasible and required by state law, depending on your state, data will be provided in a portable format. Depending on your state, you may have the right to receive additional information and it will be included in the response to your access request.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: "Correcting inaccuracies in your personal information, taking into account the information's nature processing purpose.",
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'You may request that we delete personal data about you that we maintain, subject to certain exception under applicable law.',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Opt-out of personal data processing for:',
      },
    ],
    level: 1,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'targeted advertising;',
      },
    ],
    level: 2,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'sales; or ',
      },
    ],
    level: 2,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'profiling in furtherance of decisions that produce legal or similarly significant effects.',
      },
    ],
    level: 2,
  },
  {
    type: 'list-item',
    runs: [
      {
        text: 'Either limit (opt-out of) or require consent to process sensitive personal data. ',
      },
    ],
    level: 1,
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The exact scope of these rights may vary by state. To exercise any of these rights please send an us an email stating your request to ',
      },
      {
        text: 'sarah@mycarecal.com',
        href: 'mailto:sarah@mycarecal.com',
      },
      {
        text: '.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'How we protect your perosnal data',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We use commercially reasonable administrative, physical, and technical measures designed to protect your personal data from accidental loss or destruction and from unauthorized access, use, alteration, and disclosure. However, no website, mobile application, system, electronic storage, or online service is completely secure, and we cannot guarantee the security of your personal data transmitted to, through, using, or in connection with the Website. ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'In particular, email, texts, and chats sent to or from the Website may not be secure, and you should carefully decide what information you send to us via such communications channels. Any transmission of personal data is at your own risk. Some of the information you may enter on our website may be transmitted securely via a secure medium known as Secure Sockets Layer, or SSL. Credit Card information and other sensitive information is never transmitted via email.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'The safety and security of your information also depends on you. You are responsible for taking steps to protect your personal data against unauthorized use, disclosure, and access. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'How We Retain Your Personal Data',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We keep the categories of personal data described in this policy for as long as reasonably necessary to fulfill the purposes described or for as otherwise legally permitted or required, such as maintaining the services, operating our organization, complying with our legal obligations, resolving disputes, and for safety, security, and fraud prevention. This means that we consider our legal and business obligations, potential risks of harm, and nature of the information when deciding how long to retain personal data. At the end of the retention period, personal data will be deleted, destroyed, or deidentified. ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'If you cancel your account, we generally delete your account information within approximately thirty (30) days following the effective date of cancellation. However, we may retain certain information for a longer period where necessary to comply with legal obligations, resolving disputes, for safety or fraud protection, or as required for other legitimate business purposes. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Notice to California Residents',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Residents of California may have certain rights regarding your personal information under the California Privacy Rights Act (CPRA) and the California Consumer Privacy Act (CCPA). If applicable, Company will provide you with the categories of personally identifiable information that is collected through this website and the categories of third-party entities with whom such personal information may be used at your request. California law allows you to control who we can and cannot share your personal information with. To obtain any information you are entitled to under California law, please send a request by email to: ',
      },
      {
        text: 'support@carecal.net',
        href: 'mailto:support@carecal.net',
      },
      {
        text: '. There is no charge for requesting this information from Company. ',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'Our website and services are primarily intended for residents of the United States. However, if you are within the EU, you have certain rights and are entitled to certain information under the General Data Protection Regulation (“GDPR”).',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'We will not track, sell, or otherwise use your information unless you have opted-in to do so. You have the right to object to the processing of your data at any time. Your information will not be shared with any third-parties in the EU. Your personal data is primarily used to provide you with the services and products you opt into. It may also be used to comply with legal obligations we are subject to or to fulfill our legitimate interests, such as to develop and improve our services or to detect illegal activities, or through contractual necessity. With your prior consent, it may also be used to send you targeted offers and promotions.',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'You have several rights, including the right to request access to, change, or remove and delete your personal data, or to change your marketing preferences. To obtain any information You are entitled to under the GDPR, please send a request by email to: ',
      },
      {
        text: 'support@carecal.net',
        href: 'mailto:support@carecal.net',
      },
      {
        text: '. ',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Changes to Our Privacy Policy',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'It is our policy to post any changes we make to our privacy policy on this page. If we make material changes to how we treat our users’ personal information, we will notify you by email to the email address specified in your account and/or through a notice on the Website home page. The date the privacy policy was last revised is identified at the top of the page. You are responsible for ensuring we have an up-to-date active and deliverable email address for you, and for periodically visiting our Website and this privacy policy to check for any changes.',
      },
    ],
  },
  {
    type: 'heading',
    runs: [
      {
        text: 'Contact Information',
      },
    ],
  },
  {
    type: 'paragraph',
    runs: [
      {
        text: 'To ask questions or comment about this privacy policy and our privacy practices, contact us at: ',
      },
      {
        text: 'support@carecal.net',
        href: 'mailto:support@carecal.net',
      },
      {
        text: ' ',
      },
    ],
  },
];
